import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { LatLng } from "use-places-autocomplete";
import { OnlinePaymentProvider } from "../../../utils/enums";
import {
  Franchise,
  FranchiseWithDistance,
  Restaurant,
  RestaurantConfig,
  ScheduledOrderDetailsType,
} from "../../../utils/types";
import { getRestaurantSocialMedia } from "../http/getRestaurantSocialMedia";
import {
  handleSelectPartnerRestaurant,
  handleSettingAllRestaurantDetails,
  handleSortActiveFranchises,
} from "../reduxHelpers/configReduxHelper";

export interface InitialState {
  error: string;
  restaurantConfig: RestaurantConfig | null;
  restaurantInfo: Restaurant | null;
  selectedFranchise?: Franchise;
  selectedLanguage: string;
  currency: string;
  partnerRestaurants: Restaurant[];
  activeFranchises: FranchiseWithDistance[];
  socialMedia: {
    fbId: string;
    googleTagsManager: string;
    fbDomainVerificationId: string;
    tiktokId: string;
    googleAnalytics: string;
    hasConvApiEnabled: boolean;
  };
  onlinePaymentProvider: OnlinePaymentProvider;
  showAppBanner: boolean;
  shouldClearCartDueToRestaurantSwitch: boolean;
  isRestaurantSwitchInProgress: boolean;
}

const initialState: InitialState = {
  error: "",
  restaurantInfo: null,
  partnerRestaurants: [],
  restaurantConfig: {
    qrcodeAutoSelect: false,
    settleBilleAndCompleteInOne: false,
  },

  selectedFranchise: undefined,
  activeFranchises: [],
  selectedLanguage: "en",
  currency: "$",
  socialMedia: {
    fbId: "",
    googleTagsManager: "",
    fbDomainVerificationId: "",
    tiktokId: "",
    googleAnalytics: "",
    hasConvApiEnabled: false,
  },
  showAppBanner: true,
  onlinePaymentProvider: OnlinePaymentProvider.Card,
  shouldClearCartDueToRestaurantSwitch: false,
  isRestaurantSwitchInProgress: false,
};

const config = createSlice({
  name: "config",
  initialState,
  reducers: {
    setAppBannerState: (state, action) => {
      state.showAppBanner = action.payload;
    },
    resetSelectedFranchise: (state) => {
      state.selectedFranchise = initialState.selectedFranchise;
    },
    setSelectedFranchise: (state, action) => {
      // Skip if a restaurant switch is already in progress to prevent race conditions
      if (state.isRestaurantSwitchInProgress) {
        console.log(
          "Skipping franchise selection - restaurant switch already in progress"
        );

        return;
      }

      // Validate franchise belongs to an available restaurant (current or partner)
      if (action.payload && state.restaurantInfo) {
        const allAvailableRestaurants = [
          state.restaurantInfo,
          ...state.partnerRestaurants,
        ];

        const franchiseRestaurant = allAvailableRestaurants.find(
          (restaurant) => restaurant.id === action.payload.restaurantId
        );

        // If franchise doesn't belong to any available restaurant, reject the selection
        if (!franchiseRestaurant) {
          console.warn(
            `Franchise with restaurantId ${action.payload.restaurantId} does not belong to any available restaurant. Ignoring franchise selection.`
          );

          return;
        }

        // If franchise belongs to a different restaurant than currently displayed, switch restaurant
        if (state.restaurantInfo.id !== action.payload.restaurantId) {
          handleSelectPartnerRestaurant(
            state,
            action.payload.restaurantId,
            state.restaurantInfo
          );
        }
      }

      state.selectedFranchise = action.payload;
    },

    setOnlinePaymentProvider: (state, action) => {
      state.onlinePaymentProvider = action.payload;
    },
    setAllRestaurantDetailsInRedux: (
      state,
      action: PayloadAction<Restaurant>
    ) => {
      handleSettingAllRestaurantDetails(state, action.payload);
    },

    setSelectedLanguageAndCurrency: (
      state,
      action: PayloadAction<{ currency: string; selectedLanguage: string }>
    ) => {
      state.selectedLanguage = action.payload.selectedLanguage;
      state.currency = action.payload.currency;
    },
    sortActiveFranchisesByDistance: (state, action: PayloadAction<LatLng>) => {
      handleSortActiveFranchises(state, action.payload);
    },
    setScheduledOrderFranchiseConfig: (
      state,
      { payload }: PayloadAction<Partial<ScheduledOrderDetailsType>>
    ) => {
      if (!state.selectedFranchise) {
        console.error("selectedFranchise is undefined in Redux state!");

        return;
      }

      if (!state.selectedFranchise.franchiseConfig) {
        state.selectedFranchise.franchiseConfig = {};
      }

      if (!state.selectedFranchise.franchiseConfig.scheduledOrder) {
        state.selectedFranchise.franchiseConfig.scheduledOrder = {};
      }

      state.selectedFranchise.franchiseConfig.scheduledOrder = {
        ...state.selectedFranchise.franchiseConfig.scheduledOrder,
        scheduledOrderNowCutoff: payload.scheduledOrderNowCutoff,
        scheduledOrderIftarCutoffMins: payload.scheduledOrderIftarCutoffMins,
      };
    },
    clearCartDueToRestaurantSwitchFlag: (state) => {
      state.shouldClearCartDueToRestaurantSwitch = false;
    },
    setRestaurantSwitchInProgress: (state, action: PayloadAction<boolean>) => {
      state.isRestaurantSwitchInProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getRestaurantSocialMedia.fulfilled,
      (state, { payload }: PayloadAction<any>) => {
        state.socialMedia = payload;
      }
    );
    builder.addCase(getRestaurantSocialMedia.rejected, (state) => {
      state.error = "Theres an error => getRestaurantSocialMedia";
    });
  },
});

export const {
  setSelectedFranchise,
  resetSelectedFranchise,
  setSelectedLanguageAndCurrency,
  setAllRestaurantDetailsInRedux,
  sortActiveFranchisesByDistance,
  setOnlinePaymentProvider,
  setAppBannerState,
  setScheduledOrderFranchiseConfig,
  clearCartDueToRestaurantSwitchFlag,
  setRestaurantSwitchInProgress,
} = config.actions;

export default config.reducer;
